import React, { useEffect, useRef, useCallback } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import theme from '@src/styles/theme';
import { getSourcePreparationProgress } from '@src/apis/project';
import {
  EXTRACTION_STATUS,
  FETCH_EXTRACTION_STATUS_INTERVAL,
} from '@src/constants/project';
import route from '@src/constants/route';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import AILoadingOverlay from '@src/new-components/AILoadingOverlay';
import { StyledProjectDetail } from '../index.style';
import Content from './Content';
import ProjectOnMobile from '../../Project/ProjectOnMobile';
import Header from '../component/ProjectDetailHeader';

const PresentationDetail = ({
  extractionStatus,
  onExtractionComplete,
  onGenerationComplete,
  projectId,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const intervalRef = useRef(null);
  const { getFeatureValue } = useFeatureFlags();
  const isGeneratingNarration = useSelector(
    (state) => state.project.isGeneratingNarration,
  );
  const generatingBlockIds = useSelector(
    (state) => state.project.generatingBlockIds,
  );
  const user = useSelector((state) => state.auth.user);

  const extractProgressEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_EXTRACT_PROGRESS,
    { userId: user?.id, email: user?.email, phoneNumber: user?.phoneNumber },
  );

  const isExtracting = extractionStatus === EXTRACTION_STATUS.PENDING;
  const isPreparing = isExtracting || isGeneratingNarration;

  const pollPreparationProgress = useCallback(async () => {
    const failExtraction = (message) => {
      dispatch(actions.project.setIsLoadingProjectDetail(false));
      dispatch(actions.noti.push({ severity: 'error', message }));
      history.replace(route.PROJECTS);
    };

    const failGeneration = () => {
      dispatch(actions.project.setIsGeneratingNarration(false));
      dispatch(
        actions.noti.push({ severity: 'error', message: t('errorOccurred') }),
      );
    };

    try {
      const progress = await getSourcePreparationProgress(projectId);
      const { status, generationStatus, blocks, error } =
        progress?.result || {};

      const shouldStreamBlocks =
        (isExtracting && extractProgressEnabled) || isGeneratingNarration;
      if (shouldStreamBlocks && Array.isArray(blocks)) {
        // Extraction (project load) streams every block and adopts the text as
        // the baseline. Generation only rewrites the target blocks, so stream
        // ONLY those — otherwise the streamed (stale) data would overwrite the
        // text/note + status of other blocks the user has edited but not yet
        // regenerated. Generation also must NOT sync originals, so the new
        // script reverts the block to the synthesize button.
        const blocksToStream = isExtracting
          ? blocks
          : blocks.filter((b) => generatingBlockIds?.includes(b.id));
        if (blocksToStream.length) {
          dispatch(
            actions.project.updateExtractionBlocks(blocksToStream, {
              syncOriginals: isExtracting,
            }),
          );
        }
      }

      if (isExtracting) {
        if (status === EXTRACTION_STATUS.SUCCESS) onExtractionComplete();
        else if (status === EXTRACTION_STATUS.FAILURE)
          failExtraction(error?.message || 'createProjectFailed');
      }

      if (isGeneratingNarration) {
        if (generationStatus === EXTRACTION_STATUS.SUCCESS) {
          dispatch(actions.project.setIsGeneratingNarration(false));
          onGenerationComplete?.();
        } else if (generationStatus === EXTRACTION_STATUS.FAILURE) {
          failGeneration();
        }
      }
    } catch {
      if (isExtracting) failExtraction('createProjectFailed');
      if (isGeneratingNarration) failGeneration();
    }
  }, [
    projectId,
    isExtracting,
    isGeneratingNarration,
    generatingBlockIds,
    extractProgressEnabled,
    onExtractionComplete,
    onGenerationComplete,
    history,
    dispatch,
    t,
  ]);

  useEffect(() => {
    if (!isPreparing) return undefined;
    intervalRef.current = setInterval(
      pollPreparationProgress,
      FETCH_EXTRACTION_STATUS_INTERVAL,
    );
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPreparing, pollPreparationProgress]);
  useEffect(
    () => () => {
      dispatch(actions.project.setIsGeneratingNarration(false));
    },
    [],
  );

  if (isMobile) {
    return (
      <div style={{ padding: '0 16px' }}>
        <ProjectOnMobile />
      </div>
    );
  }

  return (
    <StyledProjectDetail>
      <div className="container-main-panel" style={{ position: 'relative' }}>
        <Header />
        <Content />
        {isExtracting && !extractProgressEnabled && (
          <AILoadingOverlay text={t('aiProcessing')} blurAmount="8px" />
        )}
      </div>
    </StyledProjectDetail>
  );
};

export default PresentationDetail;
